import { Component, OnInit, ChangeDetectionStrategy, ViewChild, ElementRef } from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import { Store } from '@ngrx/store';
import { DeviceDetectorService } from 'ngx-device-detector';
import { Subscription, Observable, Subject, of } from 'rxjs';
import { filter, takeUntil, startWith, delay, skipWhile } from 'rxjs/operators';
// import { PARAMETERS } from '@angular/core/src/util/decorators';
import {
  GetProblems, getProblems, initialProblem, GetActivity
  , getActivities, UploadAttachments, UpdateActivity, GetTask, getTasks
  , UpdateActivityClose, SetNoMapLocation, DelayedAction, SetProblemMarkers, UpdateAttachment, DeleteAttachment
  , DownloadEdocsFile, getProblemAttachments, getAllUsers, GetProblemAttachments, MenuItem, GetMenuOption, getMenus, getMapState
} from 'src/app/state-management';
import { User as RapUser } from '../../state-management';
import { Problem } from 'src/app/models/problem';
import { Activity, ActivityList, TaskList } from 'src/app/models/activity';
import { UploadAttachmentsPayload, Attachment } from 'src/app/models/attachment';
import { DatePipe } from '@angular/common';
import { DnvMapState, DnvMapAction, CenterMap, SetZoom, SetShowDrawControl, PlainMapFunctionsOnly } from 'dnv-lib';
import { UntypedFormControl, UntypedFormGroup, Validators } from '@angular/forms';
import Quill  from 'quill';
import { convertQuillToHTML } from 'src/app/utils/convertQuillToHTML';

@Component({
    selector: 'app-workrequest-component',
    templateUrl: './work-request.component.html',
    styleUrls: ['./work-request.component.scss'],
    changeDetection: ChangeDetectionStrategy.OnPush,
    standalone: false
})

export class WorkRequestComponent implements OnInit {

  @ViewChild('resolution')
  public resolution: ElementRef;

  private ngUnsubscribe: Subject<void> = new Subject();

  attachments$: Observable<Attachment[]>;
  allUsers$: Observable<RapUser[]>;
  mapState$: Observable<DnvMapState>;
  problems$: Observable<Problem[]>;
  task$: Observable<TaskList[]>;
  appView$: Observable<string>;

  taskDetail: TaskList;
  problemDetail: Problem = initialProblem;
  routeSubscription: Subscription = new Subscription();
  activityNo: string;
  problemNo: string;
  activityDate: string;
  activityDateClosed: string;
  followUpDate: string;
  quillEditForm: UntypedFormGroup;
  quillNotesFocus = false;
  quillNotesEditor: Quill;
  quillNotesSaved: string;
  inProgressReOpen = false;
  inProgressComplete = false;

  constructor(
    private route: ActivatedRoute,
    private store: Store<{}>,
    private datePipe: DatePipe,
  ) {
    this.mapState$ = this.store.select(getMapState);
    this.task$ = this.store.select(getTasks);
    this.problems$ = this.store.select(getProblems);
    this.attachments$ = this.store.select(getProblemAttachments);
    this.allUsers$ = this.store.select(getAllUsers);

    if (this.route.snapshot.queryParams) {
      this.activityNo = this.route.snapshot.queryParams.ActivityNo;
      this.problemNo = this.route.snapshot.queryParams.ProblemNo;
    }
  }

  ngOnInit() {
    this.store.dispatch(new GetTask({activityNo: this.activityNo}));
    this.store.dispatch(new GetProblems({ problemNo: this.problemNo }));
    this.store.dispatch(new SetShowDrawControl(false));
    this.store.dispatch(new PlainMapFunctionsOnly(true));
    this.store.dispatch(new GetProblemAttachments(this.problemNo));

    this.quillEditForm = new UntypedFormGroup({
      'notesEditor': new UntypedFormControl('', [Validators.required])
    });

    this.task$
      .pipe(
        takeUntil(this.ngUnsubscribe)
      )
      .subscribe((tasks: TaskList[]) => {
        if (tasks.length > 0) {
          this.taskDetail = tasks[0];
          this.activityDate = this.datePipe.transform(new Date(this.taskDetail.activityDate), 'yyyy-MM-dd');
          this.followUpDate = this.taskDetail.activityFollowUpdate ? this.datePipe.transform(new Date(this.taskDetail.activityFollowUpdate), 'yyyy-MM-dd') : '';
          this.activityDateClosed = this.datePipe.transform(new Date(this.taskDetail.activityDateClosed), 'yyyy-MM-dd');
        }
      });

    this.problems$
      .pipe(
        takeUntil(this.ngUnsubscribe),
        delay(700)
      )
      .subscribe((problems: Problem[]) => {
        if (problems.length > 0) {
          this.problemDetail = problems.find(p => p.problemNo === this.problemNo);
          if (this.problemDetail.latLng.lat === 0 && this.problemDetail.latLng.lng === 0) {
            this.store.dispatch(new SetNoMapLocation(true));
          } else {
            this.store.dispatch(new SetNoMapLocation(false));
            this.store.dispatch(new DelayedAction([
              new SetProblemMarkers({ problem: [this.problemDetail], showToolTip: false }),
              new CenterMap(this.problemDetail.latLng),
              new SetZoom(Number(5))
            ]));
          }
        }
      });
  }

  onNotesEditorCreated(e: any) {
    this.quillNotesEditor = e;
    const notes = this.taskDetail.note;

    if (notes?.includes('<ul>') || notes?.includes('<ol>')) {
      const delta = this.quillNotesEditor.clipboard.convert({text: notes});
      this.quillNotesEditor.setContents(delta, 'silent');
      this.quillNotesSaved = this.quillNotesEditor.root?.innerHTML;

    } else {
      this.quillNotesEditor.setText(notes);
      this.quillNotesSaved = notes;
    }

    this.taskDetail.activityDateClosed ? this.quillNotesEditor.enable(false) : this.quillNotesEditor.enable(true);

    // disable the pen icon when not in edit mode
    const penIconElement: any = document.getElementsByClassName('fa-pen')[0];
    this.taskDetail.activityDateClosed ? penIconElement.style.display = 'none' : penIconElement.style.display = '';
  }

  updateNotes() {
    this.quillNotesFocus = false;
    const innerHTML = this.quillNotesEditor.root?.innerHTML || '';
    this.quillNotesSaved = (innerHTML.includes('<ul>') || innerHTML.includes('<ol>')) ? innerHTML : this.quillNotesEditor.getText();
    this.quillEditForm.get('notesEditor').markAsPristine();
  }

  hasNotesFocus(e: { editor: any, oldRange: any, range: any, source: string }) {
    this.quillNotesFocus = e.editor.hasFocus() ? true : false;
  }

  undoEditNotes() {
    const notes = this.quillNotesSaved;

    // if the original description used an HTML list, store as html string, otherwise store as plain text
    if (notes.includes('<ul>') || notes.includes('<ol>')) {
      const delta = this.quillNotesEditor.clipboard.convert({text: notes});
      this.quillNotesEditor.setContents(delta, 'silent');
    } else {
      this.quillNotesEditor.setText(notes);
    }

    this.quillNotesFocus = false;
    this.quillEditForm.get('notesEditor').markAsUntouched();
  }

  addAttachmentsToProblem(prob: Problem, event: UploadAttachmentsPayload) {
    const payload = Object.assign({}, event, {
      caseNo: this.problemNo,
      attachment: { files: event.attachment, state: 'files' },
      postUploadActions: event.postUploadActions,
      description: event.description
    });
    this.store.dispatch(new UploadAttachments(payload));
  }

  complete(reopen: boolean) {
    this.inProgressComplete = reopen ? false : true;
    this.inProgressReOpen = reopen ? true : false;

    const modifiedActivity: Activity = {
      activityType_RefId: this.taskDetail.activityType_Id,
      // note: this.resolution.nativeElement.value,
      note: convertQuillToHTML(this.quillNotesEditor.root?.innerHTML, this.quillNotesEditor.getText().trim()),
      user_RefId: this.taskDetail.user_Id === 0 ? null : this.taskDetail.user_Id,
      role_RefId: this.taskDetail.role_Id === 0 ? null : this.taskDetail.role_Id,
      problem_RefId: this.taskDetail.problem_Id,
      activityDate: this.taskDetail.activityDate,
      activityFollowUpdate: this.taskDetail.activityFollowUpdate,
      activityDateClosed: null,
      createdBy: this.taskDetail.createdBy_Id
    };

    modifiedActivity.activityDateClosed = reopen ? null : new Date(new Date().getFullYear(), new Date().getMonth(), new Date().getDate());
    modifiedActivity.activityDateClosed ? this.quillNotesEditor.enable(false) : this.quillNotesEditor.enable(true);

    // disable the pen icon when not in edit mode
    const penIconElement: any = document.getElementsByClassName('fa-pen')[0];
    modifiedActivity.activityDateClosed ? penIconElement.style.display = 'none' : penIconElement.style.display = '';

    this.store.dispatch(new UpdateActivityClose(
      { activityId: this.taskDetail.activity_Id, activityNo: this.activityNo, activity: modifiedActivity }));

  }

  onMapUpdate(action: any) {
    if (action.type) {
      this.store.dispatch(action);
    } else if (action.length == 1) {
      this.store.dispatch(action[0]);
    }
    else {
      action.forEach((act: DnvMapAction) => {
        this.store.dispatch(act);
      })
    }

  }

  onMoveChange(state: DnvMapState) {
    this.store.dispatch(new CenterMap({ lat: state.centerPoint.lat, lng: state.centerPoint.lng }));
  }

  onZoomChange(state: DnvMapState) {
    this.store.dispatch(new SetZoom(state.zoomLevel));
  }

  downloadEdocsFile(e: {docNum: number, desc: string}) {
    this.store.dispatch(new DownloadEdocsFile(e.docNum));
  }

  deleteAttachment(id: any) {
    this.store.dispatch(new DeleteAttachment({ attachmentId: id, caseNo: this.problemNo }));
  }

  updateAttachDescription(attachment: Attachment) {
    this.store.dispatch(new UpdateAttachment({ attachment: attachment, caseNo: this.problemNo }));
  }
}
